/**
 * FloorPlanMap — plano de planta con los elementos de una ubicación encima.
 *
 * Es UNA sola página para los dos mapas del CMDB. Se entra desde Cableado
 * (`mode="outlets"`, tomas de pared) o desde WiFi (`mode="aps"`, puntos de
 * acceso) y lo único que cambia es la fuente de datos: el resto —zoom, pantalla
 * completa, PDF de varias páginas, panel lateral plegable, arrastrar los
 * marcadores, globo con la ficha y colocación por clic— es exactamente igual.
 *
 * Las posiciones se guardan como fracción (0-1) del ancho/alto del plano, así
 * que el mismo par de coordenadas vale para cualquier tamaño de imagen o zoom.
 *
 * - Zoom con la rueda (centrado en el cursor) y con ＋ / －; el % vuelve a encajar.
 * - Arrastrar con el ratón desplaza el plano; arrastrar un marcador lo mueve.
 * - Si la ubicación tiene hijas, se muestran también SUS elementos: el plano de
 *   una sede enseña lo de todas sus plantas (salvo las que tengan plano propio,
 *   que se listan aparte para no pintarlas en el sitio equivocado).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowsPointingOutIcon,
  ArrowUpTrayIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  MagnifyingGlassMinusIcon,
  MagnifyingGlassPlusIcon,
  MapIcon,
  PencilSquareIcon,
  TrashIcon,
  VideoCameraIcon,
  WifiIcon,
} from '@heroicons/react/24/outline';
import api from '../services/api';
import Dialog from './common/Dialog';
import { pinchTransform } from '../utils/pinchZoom';
import './FloorPlanMap.css';
import './FloorPlanMapTouch.css';

const ZOOM_MIN = 0.2;
const ZOOM_MAX = 8;
const clampZoom = (z) => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, z));
const clamp01 = (v) => Math.max(0, Math.min(1, v));

const AP_STATUS_COLOR = {
  active: '#16a34a',
  maintenance: '#d97706',
  planned: '#6366f1',
  decom: '#dc2626',
};

/** Texto del marcador: el código entero no cabe y estorba al hacer zoom. */
const shortLabel = (code) => {
  const t = String(code || '').trim();
  return t.length <= 3 ? t : t.slice(-2);
};

/**
 * Las dos fuentes del mapa.
 *
 * `normalize` convierte la fila de la API en el elemento que pinta y lista la
 * página, para que el render sea uno solo:
 *   { id, code, label, room, floor, locationId, locationName, x, y,
 *     color, tone, cable, search, lines, raw }
 */
const MODES = {
  outlets: {
    title: 'Mapa de Planta · Cableado',
    Icon: MapIcon,
    noun: { one: 'toma', many: 'tomas' },
    searchPlaceholder: 'Buscar toma…',
    emptyTitle: 'Sin tomas en esta ubicación',
    assignLocationWhenEmpty: false,
    detailUrl: null,
    load: (companyId, locId) => api.getWallOutlets(companyId, {
      location_id: locId,
      include_connection: true,
      include_children: 1,
    }),
    save: (item, payload, companyId) => api.updateWallOutlet(item.id, payload, companyId),
    normalize: (o) => {
      const wired = Boolean(o.panel_port_id);
      const full = Boolean(o.connection);
      return {
        id: o.id,
        code: o.code,
        label: shortLabel(o.code),
        room: o.room,
        floor: o.floor,
        locationId: o.location_id,
        locationName: o.location_name,
        x: o.x_pos,
        y: o.y_pos,
        color: full ? '#0d9488' : wired ? '#8b5cf6' : '#6b7280',
        tone: full ? 'full' : wired ? 'wired' : 'plain',
        cable: wired
          ? (full
            ? `→ ${o.connection.other_end?.element_name || 'switch'}`
            : `→ ${o.panel_name || 'panel'} · ${o.panel_port_label || ''}`)
          : null,
        search: [o.code, o.room, o.location_name],
        lines: [
          wired
            ? { text: `${o.panel_name} · ${o.panel_port_label}`, tone: 'cable' }
            : { text: 'Sin cable de pared', tone: 'muted' },
          full
            ? { text: `${o.connection.other_end?.element_name} · ${o.connection.other_end?.label}`, tone: 'ok' }
            : (wired ? { text: 'Sin latiguillo a switch', tone: 'muted' } : null),
        ].filter(Boolean),
        raw: o,
      };
    },
  },

  aps: {
    title: 'Mapa de Planta · WiFi',
    Icon: WifiIcon,
    noun: { one: 'AP', many: 'APs' },
    searchPlaceholder: 'Buscar AP…',
    emptyTitle: 'Sin APs en esta ubicación',
    // Un AP sin ubicación se queda en la planta en la que lo sueltas.
    assignLocationWhenEmpty: true,
    detailUrl: (ap) => `/wifi/aps?destacar=${ap.id}`,
    load: (companyId, locId) => api.getWifiAPs(companyId, {
      location_id: locId,
      uplink: 1,
      include_children: 1,
    }),
    save: (item, payload, companyId) => api.updateWifiAP(item.id, payload, companyId),
    normalize: (ap) => {
      const up = ap.uplink || null;
      const wired = Boolean(up?.is_connected);
      const full = Boolean(up?.switch);
      const modelo = [ap.vendor_label || ap.vendor, ap.model].filter(Boolean).join(' · ');
      return {
        id: ap.id,
        code: ap.name,
        label: shortLabel(ap.name),
        room: ap.location_room,
        floor: ap.location_floor,
        locationId: ap.location_id,
        locationName: ap.location_name,
        x: ap.x_pos,
        y: ap.y_pos,
        color: AP_STATUS_COLOR[ap.status] || '#64748b',
        tone: full ? 'full' : wired ? 'wired' : 'plain',
        cable: full
          ? `→ ${up.switch.name} · ${up.switch_port || ''}`
          : (wired ? '→ cableado' : null),
        search: [
          ap.name, ap.code, ap.model, ap.location_room, ap.location_floor,
          ap.management_ip, ap.serial_number,
        ],
        lines: [
          modelo ? { text: modelo, tone: 'muted' } : null,
          ap.management_ip ? { text: `🌐 ${ap.management_ip}`, tone: 'muted' } : null,
          full
            ? { text: `🔌 ${up.switch_port || ''} · ${up.switch.name}`, tone: 'ok' }
            : { text: '🔌 sin cablear', tone: 'muted' },
        ].filter(Boolean),
        raw: ap,
      };
    },
  },

  cameras: {
    title: 'Mapa de Planta · Cámaras',
    Icon: VideoCameraIcon,
    noun: { one: 'cámara', many: 'cámaras' },
    searchPlaceholder: 'Buscar cámara…',
    emptyTitle: 'Sin cámaras en esta ubicación',
    // Igual que en el mapa del WiFi: se puede dar de alta la cámara desde el
    // propio plano (nombre + sala) y colocarla donde esté. El grabador y la IP
    // se ponen luego en su ficha.
    assignLocationWhenEmpty: true,
    detailUrl: (camera) => `/cameras/list?destacar=${camera.id}`,
    load: (companyId, locId) => api.getCameras(companyId, {
      location_id: locId,
      include_children: 1,
    }),
    save: (item, payload, companyId) => api.positionCamera(item.id, payload, companyId),
    normalize: (camera) => ({
      id: camera.id,
      code: camera.name,
      label: shortLabel(camera.name),
      room: camera.location_room,
      floor: camera.location_floor,
      locationId: camera.location_id,
      locationName: camera.location_name,
      x: camera.x_pos,
      y: camera.y_pos,
      color: camera.status === 'active' ? '#0ea5e9' : '#ef4444',
      tone: camera.status === 'active' ? 'full' : 'plain',
      cable: camera.recorder_name ? `→ ${camera.recorder_name}` : null,
      search: [camera.name, camera.code, camera.model, camera.ip_address, camera.location_room],
      lines: [
        camera.recorder_name ? { text: `🎞️ ${camera.recorder_name}`, tone: 'ok' } : { text: 'Sin grabador', tone: 'muted' },
        camera.ip_address ? { text: `🌐 ${camera.ip_address}`, tone: 'muted' } : null,
        camera.resolution ? { text: camera.resolution, tone: 'muted' } : null,
      ].filter(Boolean),
      raw: camera,
    }),
  },
};

function FloorPlanMap({ companyId, mode = 'outlets' }) {
  const nav = useNavigate();
  const src = MODES[mode] || MODES.outlets;

  const [locations,     setLocations]     = useState([]);
  const [selLocId,      setSelLocId]      = useState('');
  const [selLoc,        setSelLoc]        = useState(null);
  const [items,         setItems]         = useState([]);
  const [floorUrl,      setFloorUrl]      = useState(null);
  const [loading,       setLoading]       = useState(false);
  const [uploading,     setUploading]     = useState(false);
  const [pending,       setPending]       = useState(null);   // elemento a colocar con el siguiente clic
  const [activeMarker,  setActiveMarker]  = useState(null);   // id del marcador con el globo abierto
  const [savingPos,     setSavingPos]     = useState(null);   // id del elemento cuya posición se guarda
  const [dragging,      setDragging]      = useState(null);   // { id, x, y, moved }
  const [search,        setSearch]        = useState('');
  const [error,         setError]         = useState(null);
  const [annotations,   setAnnotations]   = useState([]);
  const [showNotes,     setShowNotes]     = useState(() => {
    try { return localStorage.getItem('cmdb.fpm.notes') !== 'closed'; } catch { return true; }
  });
  const [noteTool,      setNoteTool]      = useState(null);   // null | text | line | arrow
  const [noteDraft,     setNoteDraft]     = useState(null);   // { kind, x, y, x2, y2, text }
  const [noteSelected,  setNoteSelected]  = useState(null);   // { id, ... }
  const [noteEditing,   setNoteEditing]   = useState(null);   // { id, text }
  const [noteDragging,  setNoteDragging]  = useState(null);   // { id, moved, ... }
  const [planMeta,      setPlanMeta]      = useState({ is_pdf: false, pages: 1 });
  const [planPage,      setPlanPage]      = useState(1);      // página visible de un PDF
  const [pageBusy,      setPageBusy]      = useState(false);

  // Alta desde el propio plano: se crea el elemento en la ubicación que estás
  // viendo y queda «pendiente» para colocarlo tocando el mapa. Es el flujo de
  // la tablet: llegar a la sala, crear la toma y marcar dónde está.
  const [creating,      setCreating]      = useState(false);
  const [createForm,    setCreateForm]    = useState({ key: '', room: '' });
  const [createSaving,  setCreateSaving]  = useState(false);

  // Lienzo: zoom, desplazamiento y tamaño base (el plano ya encajado).
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [base, setBase] = useState({ w: 0, h: 0 });
  const zoomRef = useRef(1);
  const panRef = useRef({ x: 0, y: 0 });
  zoomRef.current = zoom;
  panRef.current = pan;

  // Panel lateral plegable y pantalla completa (se recuerdan entre sesiones).
  const [sideOpen, setSideOpen] = useState(() => {
    try { return localStorage.getItem('cmdb.fpm.side') !== 'closed'; } catch { return true; }
  });
  const [isFull, setIsFull] = useState(false);

  const fileInputRef = useRef(null);
  const prevFloorUrl = useRef(null);
  const pageRef = useRef(null);
  const viewportRef = useRef(null);
  const stageRef = useRef(null);
  const imgRef = useRef(null);
  const dragRef = useRef(null);
  const draggedRef = useRef(false);       // el plano se acaba de arrastrar
  const markerMovedRef = useRef(false);   // el marcador se acaba de arrastrar
  const pointersRef = useRef(new Map());  // pointerId → { x, y } (pellizco)
  const pinchRef = useRef(null);          // { d0, mx0, my0, z0, pan0 }
  const tapRef = useRef({ t: 0, x: 0, y: 0 });
  const panStartRef = useRef(null);       // { t, x, y } del pointerdown
  const deepLinked = useRef(false);
  const highlight = useRef(null);          // elemento a resaltar tras cargar (deeplink)

  // ── Carga de ubicaciones ─────────────────────────────────────────────────
  useEffect(() => {
    if (!companyId) return;
    try {
      const last = localStorage.getItem(`cmdb.fpm.loc:${mode}:${companyId}`);
      if (last) setSelLocId(last);
    } catch { /* sin persistencia */ }
    api.getLocations('', companyId).then(r => setLocations(r.data || [])).catch(() => {});
  }, [companyId, mode]);

  useEffect(() => {
    if (!companyId || !selLocId) return;
    try { localStorage.setItem(`cmdb.fpm.loc:${mode}:${companyId}`, selLocId); } catch { /* noop */ }
  }, [companyId, mode, selLocId]);

  // Revoke previous blob URL when it changes
  useEffect(() => {
    if (prevFloorUrl.current && prevFloorUrl.current !== floorUrl) {
      URL.revokeObjectURL(prevFloorUrl.current);
    }
    prevFloorUrl.current = floorUrl;
    return () => { if (floorUrl) URL.revokeObjectURL(floorUrl); };
  }, [floorUrl]);

  // Pantalla completa: se sigue el estado real del navegador (Esc también sale).
  useEffect(() => {
    const onChange = () => setIsFull(document.fullscreenElement === pageRef.current);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  // Deeplink ?ap=<id> (botón «Ver en el plano» de la topología WiFi)
  useEffect(() => {
    if (!src.detailUrl || deepLinked.current || !companyId || !locations.length) return;
    deepLinked.current = true;
    const apId = Number(new URLSearchParams(window.location.search).get('ap'));
    if (!apId) return;
    api.getWifiAP(apId, companyId).then(r => {
      const ap = r.data;
      if (!ap) return;
      highlight.current = ap.id;
      setActiveMarker(ap.id);
      if (ap.location_id) setSelLocId(String(ap.location_id));
    }).catch(() => {});
  }, [companyId, locations.length, src.detailUrl]);

  const toggleFullscreen = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen?.().catch(() => {});
      return;
    }
    pageRef.current?.requestFullscreen?.().catch((e) => setError(e.message));
  };

  const toggleSide = () => {
    setSideOpen((prev) => {
      const next = !prev;
      try { localStorage.setItem('cmdb.fpm.side', next ? 'open' : 'closed'); } catch { /* noop */ }
      return next;
    });
  };

  // ¿La ubicación elegida tiene hijas? Entonces el plano enseña sus elementos.
  const locationHasChildren = useMemo(
    () => locations.some((l) => String(l.parent_id) === String(selLocId)),
    [locations, selLocId],
  );

  /**
   * Carga el plano de la ubicación. Si es un PDF se pide al backend cuántas
   * páginas tiene (trae rasterizada la que se le pida) para poder navegar.
   */
  const loadPlan = useCallback(async (loc, locId, page = 1) => {
    setPlanPage(page);
    setFloorUrl(null);
    if (!loc?.floor_plan_filename) { setPlanMeta({ is_pdf: false, pages: 1 }); return; }
    const isPdf = /\.pdf$/i.test(loc.floor_plan_filename);
    let pages = 1;
    if (isPdf) {
      try {
        const meta = await api.getLocationFloorPlanMeta(locId, companyId);
        pages = Number(meta.pages) || 1;
      } catch { /* si falla, se intenta pintar igualmente */ }
    }
    setPlanMeta({ is_pdf: isPdf, pages });
    try { setFloorUrl(await api.fetchFloorPlanBlob(locId, companyId, page)); }
    catch { setFloorUrl(null); }
  }, [companyId]);

  // ── Elementos + plano de la ubicación seleccionada ────────────────────────
  const loadForLoc = useCallback(async (locId) => {
    if (!locId) {
      setItems([]); setFloorUrl(null); setSelLoc(null); setAnnotations([]);
      setPending(null); setActiveMarker(null); setPlanMeta({ is_pdf: false, pages: 1 });
      return;
    }
    setLoading(true); setError(null); setFloorUrl(null); setPending(null); setActiveMarker(null);
    setNoteDraft(null); setNoteSelected(null); setNoteEditing(null);
    const loc = locations.find(l => String(l.id) === String(locId));
    setSelLoc(loc);
    try {
      const [res, notesRes] = await Promise.all([
        src.load(companyId, locId),
        api.getFloorPlanAnnotations(locId, companyId).catch(() => ({ data: [] })),
      ]);
      const next = (res.data || []).map(src.normalize);
      setItems(next);
      setAnnotations((notesRes.data || []).filter(n => n.is_active !== false));
      if (highlight.current && next.some(i => i.id === highlight.current)) {
        setActiveMarker(highlight.current);
      }
      highlight.current = null;
      await loadPlan(loc, locId, 1);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [companyId, locations, loadPlan, src]);

  // Cambio de página dentro de un plano PDF
  const goToPage = async (next) => {
    if (!selLocId || pageBusy) return;
    const page = Math.max(1, Math.min(planMeta.pages || 1, next));
    if (page === planPage) return;
    setPageBusy(true); setError(null);
    try {
      setPlanPage(page);
      setFloorUrl(await api.fetchFloorPlanBlob(selLocId, companyId, page));
    } catch (e) { setError(e.message); }
    finally { setPageBusy(false); }
  };

  // La ubicación recordada se aplica antes de tener la lista: hay que esperar a
  // que lleguen las ubicaciones para poder resolver su plano y sus elementos.
  useEffect(() => {
    if (!selLocId) { loadForLoc(''); return; }
    if (!locations.length) return;
    loadForLoc(selLocId);
  }, [selLocId, locations.length]); // eslint-disable-line

  // ── Zoom / encaje ────────────────────────────────────────────────────────
  /** Deja el plano encajado en el hueco disponible y centrado (zoom 1 = encajado). */
  const fitPlan = useCallback(() => {
    const vp = viewportRef.current;
    if (!vp) return;
    const vw = vp.clientWidth;
    const vh = vp.clientHeight;
    if (!vw || !vh) return;
    const img = imgRef.current;
    const iw = img?.naturalWidth || 0;
    const ih = img?.naturalHeight || 0;
    if (!iw || !ih) {
      setBase({ w: vw, h: vh });
      setZoom(1);
      setPan({ x: 0, y: 0 });
      return;
    }
    const scale = Math.min(vw / iw, vh / ih);
    const w = Math.max(1, Math.round(iw * scale));
    const h = Math.max(1, Math.round(ih * scale));
    setBase({ w, h });
    setZoom(1);
    setPan({ x: Math.round((vw - w) / 2), y: Math.round((vh - h) / 2) });
  }, []);

  // Al cargar el plano y al cambiar el tamaño del hueco se vuelve a encajar...
  // salvo que el usuario haya hecho zoom: ahí se respeta lo que está mirando.
  useEffect(() => {
    const vp = viewportRef.current;
    if (!floorUrl || !vp || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(() => {
      if (zoomRef.current === 1) fitPlan();
    });
    observer.observe(vp);
    return () => observer.disconnect();
  }, [floorUrl, fitPlan]);

  /** Zoom manteniendo quieto el punto bajo el cursor (o el centro). */
  const zoomAt = useCallback((factor, clientX, clientY) => {
    const vp = viewportRef.current;
    if (!vp) return;
    const rect = vp.getBoundingClientRect();
    const cx = clientX === undefined ? rect.width / 2 : clientX - rect.left;
    const cy = clientY === undefined ? rect.height / 2 : clientY - rect.top;
    const z0 = zoomRef.current;
    const z1 = clampZoom(z0 * factor);
    if (z1 === z0) return;
    const p = panRef.current;
    const ratio = z1 / z0;
    setZoom(z1);
    setPan({ x: cx - (cx - p.x) * ratio, y: cy - (cy - p.y) * ratio });
  }, []);

  // Rueda del ratón: zoom (no scroll de la página).
  useEffect(() => {
    const vp = viewportRef.current;
    if (!vp) return undefined;
    const onWheel = (event) => {
      event.preventDefault();
      zoomAt(event.deltaY < 0 ? 1.12 : 1 / 1.12, event.clientX, event.clientY);
    };
    vp.addEventListener('wheel', onWheel, { passive: false });
    return () => vp.removeEventListener('wheel', onWheel);
  }, [zoomAt]);

  /**
   * Arrastrar el plano para moverse y pellizcar para acercar.
   *
   * Se usan Pointer Events (ratón, dedo y lápiz con el mismo código). Antes
   * solo había `mousemove`, así que en una tablet no se podía ni desplazar el
   * plano ni arrastrar un marcador. Con dos punteros se calcula el zoom por la
   * distancia entre los dedos y se mantiene quieto el punto que hay debajo.
   */
  const startPan = (event) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    const vp = viewportRef.current;
    if (!vp) return;
    pointersRef.current.set(event.pointerId, { x: event.clientX, y: event.clientY });

    if (pointersRef.current.size === 2) {
      dragRef.current = null;
      const [a, b] = [...pointersRef.current.values()];
      const rect = vp.getBoundingClientRect();
      pinchRef.current = {
        d0: Math.hypot(a.x - b.x, a.y - b.y) || 1,
        mx0: (a.x + b.x) / 2 - rect.left,
        my0: (a.y + b.y) / 2 - rect.top,
        z0: zoomRef.current,
        pan0: { ...panRef.current },
      };
      // El «clic» que llega después de un pellizco no debe colocar nada.
      draggedRef.current = true;
      return;
    }
    if (pointersRef.current.size > 2) return;

    draggedRef.current = false;
    dragRef.current = {
      pointerId: event.pointerId,
      mx: event.clientX, my: event.clientY,
      px: panRef.current.x, py: panRef.current.y,
      // Elemento pulsado: si era un marcador, un botón o un globo, este clic
      // NO debe convertirse en «colocar el elemento pendiente».
      target: event.target,
    };
    panStartRef.current = { t: performance.now(), x: event.clientX, y: event.clientY };
    try { event.currentTarget.setPointerCapture?.(event.pointerId); } catch { /* sin captura */ }
  };

  const movePan = (event) => {
    const vp = viewportRef.current;
    if (!vp || !pointersRef.current.has(event.pointerId)) return;
    pointersRef.current.set(event.pointerId, { x: event.clientX, y: event.clientY });

    // ── Pellizco ──
    if (pinchRef.current && pointersRef.current.size >= 2) {
      const [a, b] = [...pointersRef.current.values()];
      const rect = vp.getBoundingClientRect();
      const mx = (a.x + b.x) / 2 - rect.left;
      const my = (a.y + b.y) / 2 - rect.top;
      const d = Math.hypot(a.x - b.x, a.y - b.y) || 1;
      const { d0, mx0, my0, z0, pan0 } = pinchRef.current;
      const next = pinchTransform({
        z0, pan0, mx0, my0, mx, my, d0, d, min: ZOOM_MIN, max: ZOOM_MAX,
      });
      setZoom(next.zoom);
      setPan(next.pan);
      return;
    }

    const d = dragRef.current;
    if (!d || d.pointerId !== event.pointerId) return;
    const dx = event.clientX - d.mx;
    const dy = event.clientY - d.my;
    if (!draggedRef.current && (Math.abs(dx) > 5 || Math.abs(dy) > 5)) draggedRef.current = true;
    if (!draggedRef.current) return;
    setPan({ x: d.px + dx, y: d.py + dy });
  };

  const endPan = (event) => {
    pointersRef.current.delete(event.pointerId);

    if (pinchRef.current) {
      if (pointersRef.current.size < 2) {
        pinchRef.current = null;
        draggedRef.current = true;
        tapRef.current = { t: 0, x: 0, y: 0 };
      }
      return;
    }

    const d = dragRef.current;
    if (!d || d.pointerId !== event.pointerId) return;
    dragRef.current = null;

    // Ratón: el `click` se queda en el viewport por el pointer capture, así que
    // aquí se resuelve la acción del plano. Se ignora si el puntero empezó
    // sobre un control (marcador, botón, globo…) o si hubo arrastre.
    if (
      event.pointerType === 'mouse'
      && !draggedRef.current
      && (pending || noteTool || noteDraft)
      && !(d.target instanceof Element && d.target.closest(
        'button, a, input, textarea, select, .fpm-marker, .fpm-pop, .fpm-note, .fpm-note-shape',
      ))
      && applyMapAction(event.clientX, event.clientY)
    ) {
      draggedRef.current = true; // el click posterior ya no hace nada
    }

    // Doble toque = acercar en ese punto. Los navegadores móviles no emiten
    // `dblclick` de forma fiable y los botones de zoom quedan lejos del dedo.
    if (!draggedRef.current && event.pointerType !== 'mouse' && !pending && !noteTool) {
      const start = panStartRef.current;
      const now = performance.now();
      const prev = tapRef.current;
      const nearStart = start && Math.hypot(event.clientX - start.x, event.clientY - start.y) < 24;
      const isDouble = nearStart && now - prev.t < 340
        && Math.hypot(event.clientX - prev.x, event.clientY - prev.y) < 32;
      if (isDouble) {
        zoomAt(1.9, event.clientX, event.clientY);
        draggedRef.current = true; // el clic posterior no coloca ni dibuja
        tapRef.current = { t: 0, x: 0, y: 0 };
        return;
      }
      tapRef.current = { t: now, x: event.clientX, y: event.clientY };
    }
  };

  // ── Guardar / quitar posición ────────────────────────────────────────────
  const savePosition = useCallback(async (item, x, y) => {
    setSavingPos(item.id); setError(null);
    const payload = { x_pos: x, y_pos: y };
    // Un AP sin ubicación se queda en la planta en la que lo sueltas; si ya
    // colgaba de esta ubicación (o de una planta suya) NO se le cambia, porque
    // el plano que estás viendo puede ser el del edificio entero.
    if (src.assignLocationWhenEmpty && !item.raw?.location_id) payload.location_id = Number(selLocId);
    try {
      await src.save(item, payload, companyId);
      setItems(prev => prev.map(i => (i.id === item.id
        ? {
          ...i,
          x,
          y,
          locationId: payload.location_id ?? i.locationId,
          locationName: payload.location_id ? (selLoc?.name || i.locationName) : i.locationName,
        }
        : i)));
      setPending(null);
    } catch (e) { setError(e.message); }
    finally { setSavingPos(null); }
  }, [companyId, selLocId, selLoc, src]);

  const handleRemovePos = async (item, ev) => {
    if (ev) ev.stopPropagation();
    setSavingPos(item.id);
    try {
      await src.save(item, { x_pos: null, y_pos: null }, companyId);
      setItems(prev => prev.map(i => (i.id === item.id ? { ...i, x: null, y: null } : i)));
      if (pending?.id === item.id) setPending(null);
      if (activeMarker === item.id) setActiveMarker(null);
    } catch (e) { setError(e.message); }
    finally { setSavingPos(null); }
  };

  // ── Anotaciones del plano (textos, líneas y flechas) ──────────────────────
  const noteColors = ['#f8fafc', '#facc15', '#f87171', '#38bdf8'];
  const draftInputRef = useRef(null);
  const notesRef = useRef([]);
  notesRef.current = annotations;
  const draggedNoteRef = useRef(null);

  const alternarNotas = () => {
    setShowNotes(prev => {
      const next = !prev;
      try { localStorage.setItem('cmdb.fpm.notes', next ? 'open' : 'closed'); } catch { /* noop */ }
      if (!next) { setNoteTool(null); setNoteDraft(null); setNoteSelected(null); }
      return next;
    });
  };

  const activarHerramienta = (tool) => {
    setShowNotes(true);
    setPending(null);
    setNoteSelected(null);
    setNoteDraft(null);
    setNoteTool(prev => (prev === tool ? null : tool));
  };

  const guardarNota = async (payload) => {
    if (!selLocId) return null;
    try {
      const res = await api.createFloorPlanAnnotation(selLocId, payload, companyId);
      const saved = res?.data;
      if (saved) setAnnotations(prev => [...prev, saved]);
      return saved;
    } catch (e) { setError(e.message); return null; }
  };

  const actualizarNota = async (note, patch) => {
    if (!note?.id || !selLocId) return;
    setAnnotations(prev => prev.map(n => (n.id === note.id ? { ...n, ...patch } : n)));
    setNoteSelected(prev => (prev?.id === note.id ? { ...prev, ...patch } : prev));
    try {
      await api.updateFloorPlanAnnotation(selLocId, note.id, patch, companyId);
    } catch (e) { setError(e.message); }
  };

  const borrarNota = async (note) => {
    if (!note?.id || !selLocId) return;
    setAnnotations(prev => prev.filter(n => n.id !== note.id));
    if (noteSelected?.id === note.id) setNoteSelected(null);
    try { await api.deleteFloorPlanAnnotation(selLocId, note.id, companyId); }
    catch (e) { setError(e.message); }
  };

  /** Arrastrar una anotación (se mueve con el plano, no cambia de sitio). */
  const iniciarArrastreNota = (event, note) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    event.stopPropagation();
    setNoteSelected(note);
    draggedNoteRef.current = null;
    try { event.currentTarget.setPointerCapture?.(event.pointerId); } catch { /* sin captura */ }
    setNoteDragging({
      id: note.id,
      note,
      startX: event.clientX,
      startY: event.clientY,
      pointerId: event.pointerId,
      moved: false,
    });
  };

  useEffect(() => {
    if (!noteDragging) return undefined;
    const move = (ev) => {
      if (noteDragging.pointerId != null && ev.pointerId !== noteDragging.pointerId) return;
      const rect = stageRef.current?.getBoundingClientRect();
      if (!rect || !rect.width || !rect.height) return;
      const dx = (ev.clientX - noteDragging.startX) / rect.width;
      const dy = (ev.clientY - noteDragging.startY) / rect.height;
      if (!noteDragging.moved && Math.abs(ev.clientX - noteDragging.startX) < 3 && Math.abs(ev.clientY - noteDragging.startY) < 3) return;
      const base = noteDragging.note;
      const moved = {
        ...base,
        x_pos: clamp01(base.x_pos + dx),
        y_pos: clamp01(base.y_pos + dy),
      };
      if (base.kind !== 'text') {
        moved.x2_pos = clamp01((base.x2_pos ?? base.x_pos) + dx);
        moved.y2_pos = clamp01((base.y2_pos ?? base.y_pos) + dy);
      }
      if (!noteDragging.moved) setNoteDragging(d => (d ? { ...d, moved: true } : d));
      // Se guarda también en una ref: el `mouseup` llega antes de que React
      // haya vuelto a renderizar y `notesRef` todavía tendría la posición vieja.
      draggedNoteRef.current = moved;
      setAnnotations(prev => prev.map(n => (n.id === base.id ? moved : n)));
    };
    const up = () => {
      const dragging = noteDragging;
      setNoteDragging(null);
      if (!dragging.moved) return;
      const current = draggedNoteRef.current?.id === dragging.id
        ? draggedNoteRef.current
        : notesRef.current.find(n => n.id === dragging.id);
      draggedNoteRef.current = null;
      if (!current) return;
      const patch = { x_pos: current.x_pos, y_pos: current.y_pos };
      if (current.kind !== 'text') {
        patch.x2_pos = current.x2_pos;
        patch.y2_pos = current.y2_pos;
      }
      actualizarNota(current, patch);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
    };
  }, [noteDragging]); // eslint-disable-line

  /** Confirmar el texto que se está escribiendo sobre el plano. */
  const confirmarTextoNota = async () => {
    const text = (noteDraft?.text || '').trim();
    if (!text || !noteDraft) { setNoteDraft(null); return; }
    await guardarNota({
      kind: 'text',
      text,
      x_pos: noteDraft.x,
      y_pos: noteDraft.y,
      size: 2.2,
      color: '#f8fafc',
      bold: true,
      page: planMeta.pages > 1 ? planPage : 0,
    });
    setNoteDraft(null);
  };

  // Esc cancela la herramienta o el texto que estés escribiendo.
  useEffect(() => {
    const onKey = (ev) => {
      if (ev.key !== 'Escape') return;
      setNoteTool(null);
      setNoteDraft(null);
      setNoteEditing(null);
      setNoteSelected(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Clic en el plano: dibuja la anotación activa o coloca el elemento pendiente.
  //
  // OJO: el área se maneja con Pointer Events y `setPointerCapture` en el
  // viewport. En escritorio, con la captura activa, el navegador manda el
  // `click` al elemento CAPTURADO (el viewport) y no al plano, así que el
  // `onClick` del stage no se enteraba: se podía hacer pan pero NO colocar un
  // AP ni dibujar una flecha. La acción vive ahora en `applyMapAction` y se
  // llama desde el `click` del stage y, en ratón, desde `endPan`.
  const applyMapAction = (clientX, clientY) => {
    const stage = stageRef.current;
    if (!stage) return false;
    const rect = stage.getBoundingClientRect();
    if (!rect.width || !rect.height) return false;
    if (clientX < rect.left || clientX > rect.right || clientY < rect.top || clientY > rect.bottom) {
      return false;
    }
    if (noteTool) {
      const spot = {
        x: clamp01((clientX - rect.left) / rect.width),
        y: clamp01((clientY - rect.top) / rect.height),
      };
      if (noteTool === 'text') {
        setNoteDraft({ kind: 'text', ...spot, text: '' });
        setNoteSelected(null);
        setTimeout(() => draftInputRef.current?.focus(), 0);
        return true;
      }
      if (noteDraft && noteDraft.kind === noteTool) {
        guardarNota({
          kind: noteTool,
          x_pos: noteDraft.x,
          y_pos: noteDraft.y,
          x2_pos: spot.x,
          y2_pos: spot.y,
          size: 0.35,
          color: '#facc15',
          page: planMeta.pages > 1 ? planPage : 0,
        });
        setNoteDraft(null);
        return true;
      }
      setNoteDraft({ kind: noteTool, ...spot });
      setNoteSelected(null);
      return true;
    }
    if (!pending) return false;
    savePosition(
      pending,
      clamp01((clientX - rect.left) / rect.width),
      clamp01((clientY - rect.top) / rect.height),
    );
    return true;
  };

  const handleMapClick = (e) => {
    // Un clic que viene detrás de un arrastre o de un pellizco no es una acción.
    if (draggedRef.current) { draggedRef.current = false; return; }
    applyMapAction(e.clientX, e.clientY);
  };

  // ── Arrastrar un marcador dentro del plano ───────────────────────────────
  const startMarkerDrag = (item, event) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    event.stopPropagation();
    markerMovedRef.current = false;
    try { event.currentTarget.setPointerCapture?.(event.pointerId); } catch { /* sin captura */ }
    setDragging({ id: item.id, x: item.x, y: item.y, moved: false, pointerId: event.pointerId });
  };

  useEffect(() => {
    if (!dragging) return undefined;
    const move = (ev) => {
      if (dragging.pointerId != null && ev.pointerId !== dragging.pointerId) return;
      const box = stageRef.current?.getBoundingClientRect();
      if (!box || !box.width || !box.height) return;
      setDragging(d => (d
        ? { ...d, x: clamp01((ev.clientX - box.left) / box.width), y: clamp01((ev.clientY - box.top) / box.height), moved: true }
        : d));
    };
    const up = (ev) => {
      if (dragging.pointerId != null && ev.pointerId !== dragging.pointerId) return;
      const d = dragging;
      setDragging(null);
      const item = items.find(i => i.id === d.id);
      if (!item || !d.moved) return;
      markerMovedRef.current = true;
      savePosition(item, d.x, d.y);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
    };
  }, [dragging, items, savePosition]);

  // ── Plano: subir / quitar ────────────────────────────────────────────────
  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selLocId) return;
    setUploading(true); setError(null);
    try {
      const res  = await api.uploadLocationFloorPlan(selLocId, file, companyId);
      setSelLoc(res.data);
      setLocations(prev => prev.map(l => String(l.id) === selLocId ? res.data : l));
      await loadPlan(res.data, selLocId, 1);
    } catch (err) { setError(err.message); }
    finally { setUploading(false); if (e?.target) e.target.value = ''; }
  };

  const handleDeletePlan = async () => {
    if (!selLocId || !window.confirm('¿Eliminar el plano de planta de esta ubicación?')) return;
    setUploading(true);
    try {
      await api.deleteLocationFloorPlan(selLocId, companyId);
      setFloorUrl(null);
      setPlanMeta({ is_pdf: false, pages: 1 });
      setPlanPage(1);
      setSelLoc(p => ({ ...p, floor_plan_filename: null }));
      setLocations(prev => prev.map(l => String(l.id) === selLocId ? { ...l, floor_plan_filename: null } : l));
    } catch (err) { setError(err.message); }
    finally { setUploading(false); }
  };

  // ── Alta desde el plano ──────────────────────────────────────────────────
  /** Abre el alta con un código/nombre sugerido según lo que ya hay en el plano. */
  const abrirAlta = () => {
    const prefix = mode === 'aps' ? 'AP-' : (mode === 'camera' ? 'CAM-' : 'T-');
    const nums = items
      .map(it => String(it.code || '').match(/(\d+)\s*$/))
      .filter(Boolean)
      .map(m => Number(m[1]));
    const next = (nums.length ? Math.max(...nums) : 0) + 1;
    const width = mode === 'camera' ? 3 : 2;
    setCreateForm({ key: `${prefix}${String(next).padStart(width, '0')}`, room: '' });
    setCreating(true);
    setError(null);
  };

  /** Crea el elemento en la ubicación actual y lo deja listo para colocar. */
  const crearYColocar = async () => {
    const key = createForm.key.trim();
    if (!key || !selLocId || createSaving) return;
    setCreateSaving(true);
    setError(null);
    try {
      const room = createForm.room.trim();
      let neutral;
      if (mode === 'aps') {
        const res = await api.createWifiAP({
          name: key,
          location_id: Number(selLocId),
          location_room: room || undefined,
          location_floor: selLoc?.floor || undefined,
          status: 'active',
        }, companyId);
        const created = res?.data || res || {};
        if (!created.id) throw new Error('El servidor no devolvió el AP creado.');
        neutral = {
          id: created.id,
          name: key,
          code: created.code || key,
          location_id: Number(selLocId),
          location_name: created.location_name || selLoc?.name,
          location_room: room || null,
          location_floor: selLoc?.floor || null,
          status: 'active',
          uplink: null,
        };
      } else if (mode === 'camera') {
        // Alta desde el plano: solo lo imprescindible (nombre, sala y
        // ubicación). Grabador, IP y resolución se completan en su ficha.
        const res = await api.createCamera({
          name: key,
          code: key,
          location_id: Number(selLocId),
          location_room: room || undefined,
          location_floor: selLoc?.floor || undefined,
          status: 'active',
        }, companyId);
        const created = res?.data || res || {};
        if (!created.id) throw new Error('El servidor no devolvió la cámara creada.');
        neutral = {
          id: created.id,
          name: key,
          code: created.code || key,
          location_id: Number(selLocId),
          location_name: created.location_name || selLoc?.name,
          location_room: room || null,
          location_floor: selLoc?.floor || null,
          status: 'active',
          recorder_name: null,
          ip_address: null,
          resolution: null,
        };
      } else {
        const res = await api.createWallOutlet({
          code: key,
          name: key,
          room: room || undefined,
          floor: selLoc?.floor || undefined,
          location_id: Number(selLocId),
          outlet_type: 'rj45',
          status: 'active',
        }, companyId);
        const created = res?.data || res || {};
        if (!created.id) throw new Error('El servidor no devolvió la toma creada.');
        neutral = {
          id: created.id,
          code: key,
          name: key,
          room: room || null,
          floor: selLoc?.floor || null,
          location_id: Number(selLocId),
          location_name: created.location_name || selLoc?.name,
          panel_port_id: null,
          connection: null,
        };
      }
      const item = src.normalize(neutral);
      setItems(prev => [...prev, item]);
      setPending(item);
      setCreating(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setCreateSaving(false);
    }
  };

  // ── Listas ───────────────────────────────────────────────────────────────
  // Un elemento de una planta hija se pinta aquí salvo que esa planta tenga su
  // propio plano: en ese caso sus coordenadas son relativas a ese otro plano.
  const tienePlanoPropio = useCallback((id) => {
    const loc = locations.find(l => String(l.id) === String(id));
    return Boolean(loc && loc.floor_plan_filename);
  }, [locations]);

  const onThisPlan = useCallback(
    (it) => !it.locationId || String(it.locationId) === String(selLocId) || !tienePlanoPropio(it.locationId),
    [selLocId, tienePlanoPropio],
  );

  const q = search.trim().toLowerCase();
  const match = (it) => !q || it.search.some(v => String(v || '').toLowerCase().includes(q));

  const here = items.filter(it => onThisPlan(it) && match(it));
  const others = items.filter(it => !onThisPlan(it) && match(it));
  const positioned = here.filter(it => it.x != null && it.y != null);
  const unpositioned = here.filter(it => it.x == null || it.y == null);
  // Anotaciones de la página del PDF que se está viendo (page 0 = todas).
  const visibleNotes = annotations.filter(n => !n.page || n.page === planPage);
  // Con un plano de sede (tiene plantas), interesa saber de qué ubicación es
  // cada elemento: se enseña en la lista y en el globo del marcador.
  const showLocation = locationHasChildren || others.length > 0;
  const countTxt = `${here.length} ${here.length === 1 ? src.noun.one : src.noun.many}`;

  const renderRow = (it, inPlan) => (
    <div key={it.id} className="fpm-row-wrap">
      <button
        type="button"
        className={`fpm-row${pending?.id === it.id ? ' is-pending' : ''}${activeMarker === it.id ? ' is-active' : ''}${inPlan && it.tone === 'wired' ? ' is-wired' : ''}${inPlan && it.tone === 'full' ? ' is-full' : ''}`}
        onClick={() => (inPlan
          ? setActiveMarker(prev => (prev === it.id ? null : it.id))
          : setPending(prev => (prev?.id === it.id ? null : it)))}
        disabled={savingPos === it.id}
        title={`${it.code}${it.locationName ? ' · ' + it.locationName : ''}${it.room ? ' · ' + it.room : ''}${it.cable ? ' ' + it.cable : ''}`}
      >
        <span className="fpm-row-code">{it.code}</span>
        {it.room && <span className="fpm-row-meta">· {it.room}</span>}
        {showLocation && it.locationName && (
          <span className="fpm-row-loc">{it.locationName}</span>
        )}
        {it.cable && <span className={`fpm-row-cable${it.tone === 'full' ? ' is-full' : ''}`}>{it.cable}</span>}
      </button>
      {inPlan && (
        <button
          type="button"
          className="fpm-row-del"
          title="Quitar del mapa"
          onClick={ev => handleRemovePos(it, ev)}
          disabled={savingPos === it.id}
        >✕</button>
      )}
    </div>
  );

  return (
    <div ref={pageRef} className={`fpm-page${isFull ? ' is-fullscreen' : ''}`}>

      {/* Toolbar */}
      <div className="fpm-toolbar">
        <span className="fpm-title">
          <src.Icon className="cbd-ico" aria-hidden="true" />
          {src.title}
        </span>
        <button
          type="button"
          className="fpm-btn"
          onClick={toggleSide}
          aria-pressed={!sideOpen}
          title={sideOpen ? 'Ocultar el panel lateral' : 'Mostrar el panel lateral'}
        >
          {sideOpen
            ? <ChevronDoubleLeftIcon className="cbd-ico" aria-hidden="true" />
            : <ChevronDoubleRightIcon className="cbd-ico" aria-hidden="true" />}
        </button>
        <select className="fpm-select" value={selLocId} onChange={e => setSelLocId(e.target.value)}>
          <option value="">— Selecciona ubicación —</option>
          {locations.map(l => (
            <option key={l.id} value={l.id}>
              {l.name}
              {l.location_type ? ` (${l.location_type})` : ''}
              {l.floor ? ` · planta ${l.floor}` : ''}
              {l.floor_plan_filename ? ' · plano' : ''}
            </option>
          ))}
        </select>
        {selLocId && (
          <>
            <button
              type="button"
              className="fpm-btn"
              onClick={abrirAlta}
              title={`Crear ${src.noun.one} en esta ubicación y colocarla en el plano`}
              style={src.canCreate === false ? { display: 'none' } : undefined}
            >
              ＋ Añadir {src.noun.one}
            </button>
            <button type="button" className="fpm-btn is-primary" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
              <ArrowUpTrayIcon className="cbd-ico" aria-hidden="true" />
              {uploading ? 'Procesando…' : 'Subir plano'}
            </button>
            {selLoc?.floor_plan_filename && (
              <button type="button" className="fpm-btn is-danger" onClick={handleDeletePlan} disabled={uploading}>
                <TrashIcon className="cbd-ico" aria-hidden="true" />
                Quitar plano
              </button>
            )}
            {planMeta.pages > 1 && (
              <span className="fpm-pager" title="Los elementos están posicionados por ubicación: se ven igual en todas las páginas del PDF">
                <button type="button" className="fpm-btn" title="Página anterior" onClick={() => goToPage(planPage - 1)} disabled={pageBusy || planPage <= 1}>◀</button>
                <span>Página {planPage} / {planMeta.pages}{pageBusy ? ' · cargando…' : ''}</span>
                <button type="button" className="fpm-btn" title="Página siguiente" onClick={() => goToPage(planPage + 1)} disabled={pageBusy || planPage >= planMeta.pages}>▶</button>
              </span>
            )}
            <input ref={fileInputRef} type="file" accept="image/*,application/pdf,.pdf" style={{ display: 'none' }} onChange={handleUpload} />
          </>
        )}

        {floorUrl && (
          <>
            <span className="fpm-sep" />
            <button
              type="button"
              className={`fpm-btn${showNotes ? ' is-on' : ''}`}
              onClick={alternarNotas}
              title="Mostrar u ocultar las anotaciones del plano"
            >
              <PencilSquareIcon className="cbd-ico" aria-hidden="true" />
              Notas{annotations.length ? ` (${annotations.length})` : ''}
            </button>
            {showNotes && (
              <span className="fpm-zoom">
                <button
                  type="button"
                  className={`fpm-btn${noteTool === 'text' ? ' is-on' : ''}`}
                  onClick={() => activarHerramienta('text')}
                  title="Añadir un texto al plano"
                >T</button>
                <button
                  type="button"
                  className={`fpm-btn${noteTool === 'line' ? ' is-on' : ''}`}
                  onClick={() => activarHerramienta('line')}
                  title="Dibujar una línea (clic inicio, clic fin)"
                >╱</button>
                <button
                  type="button"
                  className={`fpm-btn${noteTool === 'arrow' ? ' is-on' : ''}`}
                  onClick={() => activarHerramienta('arrow')}
                  title="Dibujar una flecha (clic inicio, clic fin)"
                >➤</button>
              </span>
            )}
            {noteSelected && (
              <span className="fpm-note-tools">
                {noteColors.map(color => (
                  <button
                    key={color}
                    type="button"
                    className={`fpm-swatch${noteSelected.color === color ? ' is-on' : ''}`}
                    style={{ background: color }}
                    onClick={() => actualizarNota(noteSelected, { color })}
                    title="Cambiar el color"
                  />
                ))}
                {noteSelected.kind === 'text' && (
                  <button
                    type="button"
                    className={`fpm-btn${noteSelected.bold ? ' is-on' : ''}`}
                    onClick={() => actualizarNota(noteSelected, { bold: !noteSelected.bold })}
                    title="Negrita"
                  >B</button>
                )}
                {noteSelected.kind === 'text' && (
                  <button
                    type="button"
                    className="fpm-btn"
                    onClick={() => setNoteEditing({ id: noteSelected.id, text: noteSelected.text || '' })}
                    title="Editar el texto (en la tablet no hay doble clic)"
                  >✎</button>
                )}
                <button
                  type="button"
                  className="fpm-btn is-danger"
                  onClick={() => borrarNota(noteSelected)}
                  title="Borrar la anotación"
                >🗑</button>
              </span>
            )}
            {noteTool && (
              <span className="fpm-hint">
                {noteTool === 'text'
                  ? 'Clic en el plano para escribir'
                  : (noteDraft ? 'Clic en el punto final' : 'Clic en el punto de inicio')}
              </span>
            )}
          </>
        )}

        <span className="fpm-spacer" />
        {selLocId && !loading && (
          <span className="fpm-hint" title="Elementos colocados sobre el plano que estás viendo">
            {countTxt} · {positioned.length} colocados
          </span>
        )}
        {locationHasChildren && (
          <span className="fpm-hint" title="El plano es de una ubicación padre: se muestran también los elementos de sus plantas">
            Incluye ubicaciones hijas
          </span>
        )}
        {error && <span className="fpm-error">{error}</span>}

        {floorUrl && (
          <span className="fpm-zoom">
            <button type="button" className="fpm-btn" title="Alejar" onClick={() => zoomAt(1 / 1.2)}>
              <MagnifyingGlassMinusIcon className="cbd-ico" aria-hidden="true" />
            </button>
            <button type="button" className="fpm-btn fpm-zoom-value" title="Encajar el plano" onClick={fitPlan}>
              {Math.round(zoom * 100)}%
            </button>
            <button type="button" className="fpm-btn" title="Acercar" onClick={() => zoomAt(1.2)}>
              <MagnifyingGlassPlusIcon className="cbd-ico" aria-hidden="true" />
            </button>
          </span>
        )}
        <button
          type="button"
          className={`fpm-btn${isFull ? ' is-on' : ''}`}
          onClick={toggleFullscreen}
          title={isFull ? 'Salir de pantalla completa' : 'Pantalla completa'}
        >
          <ArrowsPointingOutIcon className="cbd-ico" aria-hidden="true" />
        </button>
      </div>

      {/* Body */}
      <div className={`fpm-body${sideOpen ? '' : ' is-side-collapsed'}`}>

        {/* LEFT SIDEBAR */}
        <aside className="fpm-side">
          {!selLocId && (
            <div className="fpm-empty-side">Selecciona una ubicación</div>
          )}
          {selLocId && loading && <div className="fpm-empty-side">Cargando…</div>}
          {selLocId && !loading && (
            <>
              <input
                className="fpm-side-search"
                type="search"
                value={search}
                placeholder={`🔍 ${src.searchPlaceholder}`}
                onChange={e => setSearch(e.target.value)}
              />
              {pending && (
                <div className="fpm-pending">
                  Toca (o haz clic en) el plano para colocar <strong>{pending.code}</strong>
                </div>
              )}
              {unpositioned.length > 0 && (
                <>
                  <div className="fpm-side-head">Sin colocar ({unpositioned.length})</div>
                  {unpositioned.map(it => renderRow(it, false))}
                  <hr className="fpm-hr" />
                </>
              )}
              {positioned.length > 0 && (
                <>
                  <div className="fpm-side-head">En plano ({positioned.length})</div>
                  {positioned.map(it => renderRow(it, true))}
                </>
              )}
              {others.length > 0 && (
                <>
                  <hr className="fpm-hr" />
                  <div className="fpm-side-head">En otras ubicaciones ({others.length})</div>
                  <div className="fpm-side-note">
                    Tienen plano propio: ábrelo en el selector para verlos colocados.
                  </div>
                  {others.map(it => (
                    <div key={it.id} className="fpm-row-wrap">
                      <button
                        type="button"
                        className="fpm-row"
                        onClick={() => setSelLocId(String(it.locationId))}
                        title={`Abrir el plano de ${it.locationName || 'su ubicación'}`}
                      >
                        <span className="fpm-row-code">{it.code}</span>
                        {it.room && <span className="fpm-row-meta">· {it.room}</span>}
                        <span className="fpm-row-loc">{it.locationName || 'otra ubicación'} ›</span>
                      </button>
                    </div>
                  ))}
                </>
              )}
              {items.length === 0 && (
                <div className="fpm-empty-side">
                  {src.emptyTitle}{locationHasChildren ? ' ni en sus hijas' : ''}
                </div>
              )}
            </>
          )}
        </aside>

        {/* MAP AREA */}
        <div
          ref={viewportRef}
          className={`fpm-viewport${pending ? ' is-placing' : ''}`}
          onPointerDown={startPan}
          onPointerMove={movePan}
          onPointerUp={endPan}
          onPointerCancel={endPan}
        >
          {!selLocId && (
            <div className="fpm-empty-canvas">
              <MapIcon className="fpm-empty-ico" aria-hidden="true" />
              <div>Selecciona una ubicación para ver su mapa de planta</div>
            </div>
          )}
          {selLocId && loading && <div className="fpm-empty-canvas">Cargando…</div>}
          {selLocId && !loading && !floorUrl && (
            <div className="fpm-empty-canvas">
              <div className="fpm-empty-title">No hay plano cargado para <strong>{selLoc?.name}</strong></div>
              <button type="button" className="fpm-btn is-primary" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
                <ArrowUpTrayIcon className="cbd-ico" aria-hidden="true" />
                Subir plano de planta (JPG / PNG / WebP / SVG / PDF)
              </button>
            </div>
          )}

          {floorUrl && (
            <div
              ref={stageRef}
              className="fpm-stage"
              style={{
                width: base.w || undefined,
                height: base.h || undefined,
                transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                opacity: base.w ? 1 : 0,
              }}
              onClick={handleMapClick}
            >
              <img
                ref={imgRef}
                src={floorUrl}
                alt="Plano de planta"
                className="fpm-plan"
                draggable={false}
                onLoad={fitPlan}
              />

              {/* Anotaciones: van DENTRO del plano, así que se mueven y escalan con él. */}
              {showNotes && visibleNotes.some(n => n.kind !== 'text') && (
                <svg
                  className="fpm-notes"
                  viewBox={`0 0 ${base.w || 1000} ${base.h || 1000}`}
                  preserveAspectRatio="none"
                  aria-hidden="true"
                >
                  {visibleNotes.filter(n => n.kind !== 'text').map(n => {
                    const W = base.w || 1000;
                    const H = base.h || 1000;
                    const x1 = n.x_pos * W;
                    const y1 = n.y_pos * H;
                    const x2 = (n.x2_pos ?? n.x_pos) * W;
                    const y2 = (n.y2_pos ?? n.y_pos) * H;
                    const stroke = Math.max(1.5, ((n.size || 0.35) / 100) * W);
                    const head = stroke * 3.4;
                    const angle = Math.atan2(y2 - y1, x2 - x1);
                    return (
                      <g key={n.id} className="fpm-note-shape" onClick={ev => { ev.stopPropagation(); setNoteSelected(n); }}>
                        <line
                          x1={x1} y1={y1} x2={x2} y2={y2}
                          stroke={n.color} strokeWidth={stroke} strokeLinecap="round"
                        />
                        {n.kind === 'arrow' && (
                          <polygon
                            fill={n.color}
                            points={`${x2},${y2} ${x2 - head * Math.cos(angle - 0.45)},${y2 - head * Math.sin(angle - 0.45)} ${x2 - head * Math.cos(angle + 0.45)},${y2 - head * Math.sin(angle + 0.45)}`}
                          />
                        )}
                      </g>
                    );
                  })}
                </svg>
              )}

              {showNotes && visibleNotes.filter(n => n.kind === 'text').map(n => (
                <div
                  key={n.id}
                  className={`fpm-note${noteSelected?.id === n.id ? ' is-selected' : ''}${noteDragging?.id === n.id ? ' is-dragging' : ''}`}
                  style={{
                    left: `${(n.x_pos * 100).toFixed(3)}%`,
                    top: `${(n.y_pos * 100).toFixed(3)}%`,
                    color: n.color,
                    fontWeight: n.bold ? 700 : 500,
                    fontSize: `${(((n.size || 2.2) / 100) * (base.w || 1000)).toFixed(1)}px`,
                  }}
                  onPointerDown={ev => iniciarArrastreNota(ev, n)}
                  onClick={ev => { ev.stopPropagation(); setNoteSelected(n); }}
                  onDoubleClick={ev => { ev.stopPropagation(); setNoteEditing({ id: n.id, text: n.text || '' }); }}
                  title={`${n.text || ''} · arrastra para mover · ✎ para editar`}
                >
                  {noteEditing?.id === n.id ? (
                    <input
                      className="fpm-note-input"
                      value={noteEditing.text}
                      autoFocus
                      onChange={ev => setNoteEditing({ id: n.id, text: ev.target.value })}
                      onMouseDown={ev => ev.stopPropagation()}
                      onClick={ev => ev.stopPropagation()}
                      onBlur={() => {
                        const text = (noteEditing.text || '').trim();
                        if (text && text !== n.text) actualizarNota(n, { text });
                        setNoteEditing(null);
                      }}
                      onKeyDown={ev => {
                        if (ev.key === 'Enter') {
                          ev.preventDefault();
                          const text = (noteEditing.text || '').trim();
                          if (text && text !== n.text) actualizarNota(n, { text });
                          setNoteEditing(null);
                        }
                        if (ev.key === 'Escape') { ev.stopPropagation(); setNoteEditing(null); }
                      }}
                    />
                  ) : (n.text)}
                </div>
              ))}

              {noteDraft?.kind === 'text' && (
                <input
                  ref={draftInputRef}
                  className="fpm-note-input is-draft"
                  style={{
                    left: `${(noteDraft.x * 100).toFixed(3)}%`,
                    top: `${(noteDraft.y * 100).toFixed(3)}%`,
                    fontSize: `${((2.2 / 100) * (base.w || 1000)).toFixed(1)}px`,
                  }}
                  value={noteDraft.text}
                  placeholder="Texto… (Enter para guardar)"
                  onChange={ev => setNoteDraft(d => ({ ...d, text: ev.target.value }))}
                  onMouseDown={ev => ev.stopPropagation()}
                  onClick={ev => ev.stopPropagation()}
                  onKeyDown={ev => {
                    if (ev.key === 'Enter') { ev.preventDefault(); confirmarTextoNota(); }
                    if (ev.key === 'Escape') { ev.stopPropagation(); setNoteDraft(null); }
                  }}
                />
              )}

              {/* Marcadores */}
              {positioned.map(it => {
                const drag = dragging?.id === it.id ? dragging : null;
                const x = drag ? drag.x : it.x;
                const y = drag ? drag.y : it.y;
                const isHL = activeMarker === it.id || savingPos === it.id;
                return (
                  <div
                    key={it.id}
                    role="button"
                    tabIndex={0}
                    className={`fpm-marker${isHL ? ' is-active' : ''}${drag ? ' is-dragging' : ''}`}
                    style={{
                      left: `${(x * 100).toFixed(3)}%`,
                      top: `${(y * 100).toFixed(3)}%`,
                      background: it.color,
                      // El marcador no crece con el zoom: se queda legible.
                      '--fpm-inv': 1 / zoom,
                    }}
                    onPointerDown={ev => startMarkerDrag(it, ev)}
                    onClick={ev => {
                      ev.stopPropagation();
                      // Si acabas de arrastrarlo, el clic no abre/cierra el globo.
                      if (markerMovedRef.current) { markerMovedRef.current = false; return; }
                      setActiveMarker(prev => (prev === it.id ? null : it.id));
                    }}
                    onKeyDown={ev => {
                      if (ev.key !== 'Enter' && ev.key !== ' ') return;
                      ev.preventDefault();
                      ev.stopPropagation();
                      setActiveMarker(prev => (prev === it.id ? null : it.id));
                    }}
                    title={`${it.code}${it.locationName ? ' · ' + it.locationName : ''}${it.room ? ' · ' + it.room : ''}${it.cable ? ' · ' + it.cable.replace('→ ', '') : ''}`}
                  >
                    <span className="fpm-marker-txt">{it.label}</span>

                    {activeMarker === it.id && (
                      <span className="fpm-pop" onClick={ev => ev.stopPropagation()}>
                        <strong>{it.code}</strong>
                        {it.locationName && <span className="fpm-pop-line">📍 {it.locationName}</span>}
                        {(it.room || it.floor) && (
                          <span className="fpm-pop-line">
                            🏢 {[it.room, it.floor].filter(Boolean).join(' · ')}
                          </span>
                        )}
                        {it.lines.map((line, i) => (
                          <span
                            key={i}
                            className={`fpm-pop-line${line.tone === 'muted' ? ' is-muted' : line.tone === 'cable' ? ' is-cable' : line.tone === 'ok' ? ' is-ok' : ''}`}
                          >
                            {line.text}
                          </span>
                        ))}
                        <span className="fpm-pop-actions">
                          {src.detailUrl && (
                            <button
                              type="button"
                              className="fpm-pop-go"
                              onClick={ev => { ev.stopPropagation(); nav(src.detailUrl(it.raw)); }}
                            >
                              Abrir ficha ↗
                            </button>
                          )}
                          <button
                            type="button"
                            className="fpm-pop-del"
                            onClick={ev => handleRemovePos(it, ev)}
                          >
                            ✕ Quitar del mapa
                          </button>
                        </span>
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Alta rápida: crea en la ubicación que estás viendo y deja el elemento
          pendiente para colocarlo con el siguiente toque en el plano. */}
      {creating && (
        <Dialog
          title={`Nueva ${src.noun.one}`}
          icon="➕"
          onClose={() => setCreating(false)}
          busy={createSaving}
          maxWidth={420}
          footer={(
            <>
              <button type="button" className="list-btn-secondary" onClick={() => setCreating(false)} disabled={createSaving}>
                Cancelar
              </button>
              <button
                type="button"
                className="list-btn-primary"
                onClick={crearYColocar}
                disabled={createSaving || !createForm.key.trim()}
              >
                {createSaving ? 'Creando…' : 'Crear y colocar'}
              </button>
            </>
          )}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <label htmlFor="fpm-new-key" style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)' }}>
              {mode === 'aps' ? 'Nombre del AP' : (mode === 'camera' ? 'Nombre de la cámara' : 'Código de la toma')}
            </label>
            <input
              id="fpm-new-key"
              className="list-input"
              value={createForm.key}
              autoFocus
              onChange={e => setCreateForm(f => ({ ...f, key: e.target.value }))}
              onKeyDown={e => { if (e.key === 'Enter') crearYColocar(); }}
            />
            <label htmlFor="fpm-new-room" style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)' }}>
              Sala {mode === 'outlets' ? '/ despacho ' : ''}(opcional)
            </label>
            <input
              id="fpm-new-room"
              className="list-input"
              value={createForm.room}
              onChange={e => setCreateForm(f => ({ ...f, room: e.target.value }))}
              onKeyDown={e => { if (e.key === 'Enter') crearYColocar(); }}
            />
            <p style={{ margin: 0, fontSize: '0.76rem', lineHeight: 1.45, color: 'var(--text-muted)' }}>
              Se crea en <strong>{selLoc?.name || 'esta ubicación'}</strong>. Después,
              toca el plano donde está y quedará colocada.
            </p>
          </div>
        </Dialog>
      )}
    </div>
  );
}

export default FloorPlanMap;
